// forked and modified from 'https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/modules/Link.js'
// usage - https://reacttraining.com/react-router/web/api/Link

import * as React from 'react'
import { connect } from 'react-redux'
import * as invariant from 'invariant'
import history, { getLocationPath } from 'common/history'
import { setScrollKept } from 'common/ducks/scroll/scrollKept'
import { setScrollRestoredSelf } from 'common/ducks/scroll/scrollRestoredSelf'
import { setScrollPositionHistory } from 'common/utils/scrollUtils'
import { isIe10, isIe9 } from '../utils/oldIeVersion'
import { sendClickLog } from 'common/services/product'
import { replaceUrl } from 'common/utils/url'

const isModifiedEvent = event => !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)

interface DispatchProps {
  setScrollKept: typeof setScrollKept
  setScrollRestoredSelf: typeof setScrollRestoredSelf
}

interface OwnProps {
  to: string // to type 을 `string | LocationDescriptorObject` 로 설정 시, history.replace/push 에서 exception 남. typescript 제한인 듯
  onClick?: Function
  replace?: boolean
  target?: string
  innerRef?: () => void // string refs are considered legacy (https://reactjs.org/docs/refs-and-the-dom.html#legacy-api-string-refs)
  className?: string
  style?: object
  scrollKept?: boolean
  scrollRestoredSelf?: boolean
  role?: string
  ariaSelected?: boolean
  ariaCurrent?: boolean
  ariaDisabled?: boolean
  isInfinitePage?: boolean
  executeATag?: boolean
  clickLogId?: string
  restorationHash?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  replaceUrl?: (event) => string
}

type Props = DispatchProps & OwnProps

class Link extends React.Component<Props, {}> {
  handleClick = event => {
    const nextUrl = (this.props.replaceUrl && this.props.replaceUrl(event)) || this.props.to

    if (this.props.target) {
      return
    }

    if (getLocationPath(history.location) === nextUrl) {
      event.preventDefault()
      return
    }

    // NaPm 파라미터 경우 클릭할때마다 값이 바뀌어 연속적으로 클릭되었을때 방어가 불가하므로 예외처리를 진행.
    if (nextUrl.includes('NaPm') && history.location.pathname === this.props.to) {
      event.preventDefault()
      return
    }

    if (this.props.clickLogId) {
      sendClickLog(this.props.clickLogId)
    }

    if (this.props.onClick && this.props.onClick(event)) {
      event.preventDefault()
      return
    }

    if (!this.props.isInfinitePage) {
      setScrollPositionHistory(getLocationPath(history.location))
    }

    if (this.props.executeATag) {
      const { restorationHash } = this.props
      if (restorationHash) {
        const hash = restorationHash === 'scrollY' ? `scrollY=${window.scrollY}` : restorationHash
        replaceUrl(null, hash)
      }
      return
    }

    // IE9/10 에서 IFRAME 이 HTML document, Flash Object 를 처리하는 페이지에서, client-side rendering 만 발생시켜 다른 페이지를 이동하면, "__flash__removecallback__" 에러 발생
    // https://stackoverflow.com/questions/15887284/flash-removecallback-is-undefined-errors-in-ie9-while-removing-the-dom-elemen
    // (임시) IE9/10 에서는 페이지 이동 시, 매번 server-side rendering 이 발생하도록 설정
    if (isIe9() || isIe10()) {
      return
    }

    if (
      !event.defaultPrevented && // onClick prevented default
      event.button === 0 && // ignore everything but left clicks
      !isModifiedEvent(event) // ignore clicks with modifier keys
    ) {
      event.preventDefault()
      if (this.props.scrollKept) {
        this.props.setScrollKept(this.props.scrollKept)
      }
      this.props.scrollRestoredSelf && this.props.setScrollRestoredSelf(this.props.scrollRestoredSelf)
      window.setTimeout(() => {
        // show :active style of <a>
        const { replace } = this.props
        if (replace) {
          history.replace(nextUrl)
        } else {
          history.push(nextUrl)
        }
      }, 20)
    }
  }

  render() {
    const {
      to,
      children,
      innerRef,
      className,
      role,
      ariaSelected,
      ariaCurrent,
      ariaDisabled,
      style,
      target,
    } = this.props

    invariant(to !== undefined, 'You must specify the "to" property')

    return (
      <a
        href={to}
        target={target}
        onClick={this.handleClick}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        ref={innerRef}
        className={`${className || ''} linkAnchor`}
        role={role}
        aria-selected={ariaSelected}
        aria-current={ariaCurrent}
        aria-disabled={ariaDisabled}
        style={style}>
        {children}
      </a>
    )
  }
}

export default connect<{}, DispatchProps, OwnProps>(
  undefined,
  {
    setScrollKept,
    setScrollRestoredSelf,
  },
)(Link)
